import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HelpBanner,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { Ban, Pencil, Plus, RotateCcw, TriangleAlert } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { useGamesReference } from '../hooks/useReference'
import { messageOf } from '../lib/apiError'
import {
  CatalogToolbar,
  ErrorBanner,
  PageHeading,
  Pagination,
  StatusBadge,
} from '../components/catalog'
import { Combobox } from '../components/controls'

export interface Game {
  gameId: number
  gameName: string
  brandId: number
  brand: string
  gameTypeId: number
  gameType: string
  gameSubTypeId: number
  gameSubType: string
  /** The type the SUB-TYPE belongs to; 33 production rows disagree with gameTypeId. */
  subTypeGameTypeId: number
  releaseDate: string | null
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

interface GamePage {
  games: Game[]
  total: number
  offset: number
  limit: number
}

interface FormValue {
  gameName: string
  brandId: string
  gameTypeId: string
  gameSubTypeId: string
  releaseDate: string
}

const emptyForm: FormValue = {
  gameName: '',
  brandId: '',
  gameTypeId: '',
  gameSubTypeId: '',
  releaseDate: '',
}

const PAGE_SIZE = 50

/** The API sends an ISO timestamp; the form edits a plain date. */
const dateOnly = (iso: string | null) => (iso ? iso.slice(0, 10) : '')

/**
 * GamesPage is the game catalogue — [GameHub].[Game], 9054 rows and the largest
 * this backoffice serves.
 *
 * Which is why it never renders unfiltered: the page is search-first, paged at
 * 50, and filterable by brand, type and sub-type. Names are no help as an
 * identifier — 1526 of them repeat, 148 inside a single brand — so the id
 * column stays visible and the sort falls back to it.
 */
export function GamesPage() {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')
  const { brands, gameTypes, gameSubTypes } = useGamesReference()

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [brandFilter, setBrandFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [subTypeFilter, setSubTypeFilter] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Game | null>(null)
  const [form, setForm] = useState<FormValue>(emptyForm)
  const [error, setError] = useState('')

  const params = {
    search,
    includeDisabled: String(includeDisabled),
    offset,
    limit: PAGE_SIZE,
    ...(brandFilter ? { brandId: brandFilter } : {}),
    ...(typeFilter ? { gameTypeId: typeFilter } : {}),
    ...(subTypeFilter ? { gameSubTypeId: subTypeFilter } : {}),
  }

  const { data, isLoading } = useQuery({
    queryKey: ['games', params],
    queryFn: async (): Promise<GamePage> => {
      const res = await api.get('/games', { params })
      return res.data as GamePage
    },
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['games'] })
  const set = (patch: Partial<FormValue>) => setForm((f) => ({ ...f, ...patch }))

  const openCreate = () => {
    setEditing(null)
    setForm({
      ...emptyForm,
      brandId: brandFilter,
      gameTypeId: typeFilter,
      gameSubTypeId: subTypeFilter,
    })
    setError('')
    setDialogOpen(true)
  }

  const openEdit = (row: Game) => {
    setEditing(row)
    setForm({
      gameName: row.gameName,
      brandId: String(row.brandId),
      gameTypeId: String(row.gameTypeId),
      gameSubTypeId: String(row.gameSubTypeId),
      releaseDate: dateOnly(row.releaseDate),
    })
    setError('')
    setDialogOpen(true)
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        gameName: form.gameName.trim(),
        brandId: Number(form.brandId),
        gameTypeId: Number(form.gameTypeId),
        gameSubTypeId: Number(form.gameSubTypeId),
        // A date with no time is midnight UTC, which is how the column reads.
        releaseDate: form.releaseDate ? `${form.releaseDate}T00:00:00Z` : null,
        isDisabledByPlatform: editing?.isDisabledByPlatform ?? false,
      }
      return editing ? api.put(`/games/${editing.gameId}`, body) : api.post('/games', body)
    },
    onSuccess: () => {
      setDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('games.errors.save'))),
  })

  const toggle = useMutation({
    mutationFn: (row: Game) =>
      row.isDisabledByPlatform
        ? api.post(`/games/${row.gameId}/restore`)
        : api.delete(`/games/${row.gameId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('games.errors.save'))),
  })

  const games = data?.games ?? []
  const total = data?.total ?? 0

  const brandItems = brands.map((b) => ({ value: String(b.id), label: b.name }))
  const typeItems = gameTypes.map((g) => ({ value: String(g.id), label: g.name }))

  // The sub-type picker follows the chosen type: pairing one with a sub-type of
  // another type is exactly what the API refuses, so the form does not offer it.
  const subTypeItems = useMemo(() => {
    const typeId = Number(form.gameTypeId)
    return gameSubTypes
      .filter((s) => !typeId || s.gameTypeId === typeId)
      .map((s) => ({ value: String(s.id), label: s.name }))
  }, [gameSubTypes, form.gameTypeId])

  const filterSubTypeItems = useMemo(() => {
    const typeId = Number(typeFilter)
    return gameSubTypes
      .filter((s) => !typeId || s.gameTypeId === typeId)
      .map((s) => ({ value: String(s.id), label: s.name }))
  }, [gameSubTypes, typeFilter])

  const canSubmit =
    form.gameName.trim() !== '' &&
    form.brandId !== '' &&
    form.gameTypeId !== '' &&
    form.gameSubTypeId !== ''

  return (
    <div>
      <PageHeading
        title={t('games.title')}
        subtitle={t('games.subtitle')}
        action={
          canWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger
                render={
                  <Button onClick={openCreate}>
                    <Plus />
                    {t('games.newGame')}
                  </Button>
                }
              />
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editing ? t('games.editGame') : t('games.newGame')}</DialogTitle>
                </DialogHeader>
                <form
                  className="flex flex-col gap-4"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (canSubmit) save.mutate()
                  }}
                >
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="game-name">{t('games.columns.name')}</Label>
                    <Input
                      id="game-name"
                      value={form.gameName}
                      onChange={(e) => set({ gameName: e.target.value })}
                      maxLength={100}
                      placeholder="Book of Futuria"
                      autoFocus
                    />
                    <p className="text-xs text-muted-foreground">{t('games.nameHint')}</p>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label>{t('games.columns.brand')}</Label>
                    <Combobox
                      value={form.brandId}
                      onValueChange={(v) => set({ brandId: v })}
                      items={brandItems}
                      placeholder={t('games.pickBrand')}
                      className="w-full"
                    />
                  </div>

                  <div className="flex gap-3">
                    <div className="flex flex-1 flex-col gap-1.5">
                      <Label>{t('games.columns.type')}</Label>
                      <Combobox
                        value={form.gameTypeId}
                        onValueChange={(v) =>
                          // Changing the type invalidates the sub-type below it.
                          set({ gameTypeId: v, gameSubTypeId: '' })
                        }
                        items={typeItems}
                        placeholder={t('games.pickType')}
                        className="w-full"
                      />
                    </div>
                    <div className="flex flex-1 flex-col gap-1.5">
                      <Label>{t('games.columns.subType')}</Label>
                      <Combobox
                        value={form.gameSubTypeId}
                        onValueChange={(v) => set({ gameSubTypeId: v })}
                        items={subTypeItems}
                        placeholder={t('games.pickSubType')}
                        className="w-full"
                      />
                    </div>
                  </div>
                  <p className="-mt-2 text-xs text-muted-foreground">{t('games.pairingHint')}</p>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="game-release">{t('games.columns.release')}</Label>
                    <Input
                      id="game-release"
                      type="date"
                      value={form.releaseDate}
                      onChange={(e) => set({ releaseDate: e.target.value })}
                      className="w-48"
                    />
                    <p className="text-xs text-muted-foreground">{t('games.releaseHint')}</p>
                  </div>

                  <ErrorBanner message={error} />

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit" disabled={save.isPending || !canSubmit}>
                      {editing ? t('common.save') : t('common.create')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <HelpBanner storageKey="games">
        <p>{t('games.help.line1')}</p>
        <p>{t('games.help.line2')}</p>
      </HelpBanner>

      <CatalogToolbar
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onSubmitSearch={() => {
          setSearch(searchInput)
          setOffset(0)
        }}
        searchPlaceholder={t('games.searchPlaceholder')}
        includeDisabled={includeDisabled}
        onToggleDisabled={() => {
          setIncludeDisabled(!includeDisabled)
          setOffset(0)
        }}
        filtered={
          search !== '' ||
          !includeDisabled ||
          brandFilter !== '' ||
          typeFilter !== '' ||
          subTypeFilter !== ''
        }
        onClear={() => {
          setSearch('')
          setSearchInput('')
          setBrandFilter('')
          setTypeFilter('')
          setSubTypeFilter('')
          setIncludeDisabled(true)
          setOffset(0)
        }}
      >
        <Combobox
          value={brandFilter}
          onValueChange={(v) => {
            setBrandFilter(v)
            setOffset(0)
          }}
          items={brandItems}
          placeholder={t('games.allBrands')}
          className="w-48"
        />
        <Combobox
          value={typeFilter}
          onValueChange={(v) => {
            setTypeFilter(v)
            setSubTypeFilter('')
            setOffset(0)
          }}
          items={typeItems}
          placeholder={t('games.allTypes')}
          className="w-44"
        />
        <Combobox
          value={subTypeFilter}
          onValueChange={(v) => {
            setSubTypeFilter(v)
            setOffset(0)
          }}
          items={filterSubTypeItems}
          placeholder={t('games.allSubTypes')}
          className="w-44"
        />
      </CatalogToolbar>

      {!dialogOpen && <ErrorBanner message={error} />}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-20">{t('games.columns.id')}</TableHead>
                  <TableHead>{t('games.columns.name')}</TableHead>
                  <TableHead className="w-44">{t('games.columns.brand')}</TableHead>
                  <TableHead className="w-36">{t('games.columns.type')}</TableHead>
                  <TableHead className="w-36">{t('games.columns.subType')}</TableHead>
                  <TableHead className="w-28">{t('games.columns.release')}</TableHead>
                  <TableHead className="w-28">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {games.map((row) => (
                  <TableRow key={row.gameId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {row.gameId}
                    </TableCell>
                    <TableCell className="font-medium">{row.gameName}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.brand || `#${row.brandId}`}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {row.gameType || `#${row.gameTypeId}`}
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5">
                        <Badge variant="outline">
                          {row.gameSubType || `#${row.gameSubTypeId}`}
                        </Badge>
                        {row.subTypeGameTypeId !== 0 &&
                          row.subTypeGameTypeId !== row.gameTypeId && (
                            <TriangleAlert
                              className="size-3.5 shrink-0 text-amber-600"
                              // 33 production games declare a type their
                              // sub-type does not belong to; the page says so
                              // rather than rendering a pairing that is not real.
                              aria-label={t('games.mismatch')}
                            />
                          )}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {dateOnly(row.releaseDate) || '—'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={row.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEdit(row)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={
                              row.isDisabledByPlatform ? t('common.restore') : t('common.retire')
                            }
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(row)}
                          >
                            {row.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {games.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 8 : 7}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('games.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            total={total}
            offset={offset}
            pageSize={PAGE_SIZE}
            onOffset={setOffset}
            label={t('games.total', { count: total })}
          />
        </>
      )}
    </div>
  )
}
